import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  isFetching?: boolean;
}

/**
 * Windowed pagination nav. Returns null when totalPages <= 1.
 * Always shows page 1 and last page; shows current ±1; uses ellipsis for gaps.
 * When totalPages <= 7 shows every page without ellipsis.
 * Below `sm` the numbers collapse to a "page / total" label between the arrows.
 */
export function Pagination({ page, totalPages, onChange, isFetching }: PaginationProps) {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  // Build the list of page numbers (and "..." sentinels) to render.
  function buildWindow(): Array<number | "..."> {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items: Array<number | "..."> = [];
    // Always include page 1.
    items.push(1);

    // Gap before the window?
    if (page - 1 > 2) {
      items.push("...");
    }

    // Window: page-1, page, page+1 (clamped, deduped with first/last).
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) {
      items.push(p);
    }

    // Gap after the window?
    if (page + 1 < totalPages - 1) {
      items.push("...");
    }

    // Always include last page.
    items.push(totalPages);

    return items;
  }

  const window = buildWindow();

  return (
    <nav
      aria-label={t("customers.pagination")}
      className={`flex flex-none items-center gap-1 ${isFetching ? "opacity-70" : ""}`}
    >
      {/* Previous button */}
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        aria-label={t("customers.prevPage")}
        className="h-9 w-9 rounded-lg border border-grey-200 bg-white text-[13px] font-semibold text-grey-600 hover:bg-grey-50 active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-40 sm:h-8 sm:w-8"
      >
        <ChevronLeft size={16} className="mx-auto" />
      </button>

      <span aria-live="polite" className="tnum min-w-[52px] px-1 text-center text-[13px] font-semibold text-grey-700 sm:hidden">
        {page} / {totalPages}
      </span>

      {/* Page number buttons and ellipsis spans */}
      {window.map((item, idx) =>
        item === "..." ? (
          <span
            key={`ellipsis-${idx}`}
            aria-hidden
            className="hidden h-8 w-8 items-center justify-center text-[13px] text-grey-400 sm:flex"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            aria-label={t("customers.pageLabel", { page: item })}
            aria-current={item === page ? "page" : undefined}
            className={`hidden h-8 w-8 rounded-lg border text-[13px] font-semibold sm:block ${
              item === page
                ? "border-green bg-green text-white"
                : "border-grey-200 bg-white text-grey-600 hover:bg-grey-50"
            }`}
          >
            {item}
          </button>
        ),
      )}

      {/* Next button */}
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        aria-label={t("customers.nextPage")}
        className="h-9 w-9 rounded-lg border border-grey-200 bg-white text-[13px] font-semibold text-grey-600 hover:bg-grey-50 active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-40 sm:h-8 sm:w-8"
      >
        <ChevronRight size={16} className="mx-auto" />
      </button>
    </nav>
  );
}
